import React, { memo } from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
    Navigation,
    Autoplay,
    Scrollbar,
    A11y,
    Keyboard,
    EffectCoverflow,
    Mousewheel,
    Pagination,
} from 'swiper/modules';
// swiper CSS is imported at the webpack entry point (src/jsx/Antena1.jsx) to ensure
// it always lands in bundle.css regardless of build mode.

/*
 * React Swiper third lib reusable component
 * Documentation (swiper react): https://swiperjs.com/react
 * Website: https://swiperjs.com/
 * GitHub: https://github.com/nolimits4web/swiper
 *
 * Migration notes (v6 → v12):
 * - SwiperCore.use() removed; modules passed via <Swiper modules={[...]}> prop
 * - Lazy module removed (built into core); lazy prop still works natively
 * - preloadImages and watchSlidesVisibility params removed in v9
 */

const SWIPER_MODULES = [
    Navigation,
    Autoplay,
    Scrollbar,
    A11y,
    Keyboard,
    EffectCoverflow,
    Mousewheel,
    Pagination,
];

const getNavigationConfig = (navigation) => {
    if (!navigation) {
        return false;
    }

    if (typeof navigation === 'object') {
        return {
            addIcons: false,
            ...navigation,
        };
    }

    return {
        addIcons: false,
    };
};

const Slider = memo(({
    autoplay = true,
    slidesPerView = 1,
    grabCursor = true,
    initialSlide = 0,
    clickable = true,
    loop = false,
    keyboard = true,
    watchSlidesProgress = true,
    lazy = {
        loadPrevNext: true,
    },
    navigation = false,
    breakpoints,
    preventClicks = true,
    preventClicksPropagation = true,
    mousewheel = false,
    effect = 'slide',
    centeredSlides = false,
    spaceBetween = 0,
    coverflowEffect,
    slideActiveClass = 'swiper-slide-active',
    containerClass,
    pagination = false,
    onSlideChange = () => {},
    speed = 1000,
    onSwiper = () => {},
    children,
}) => (
    <Swiper
        modules={SWIPER_MODULES}
        autoplay={autoplay}
        slidesPerView={slidesPerView}
        grabCursor={grabCursor}
        clickable={clickable.toString()}
        initialSlide={initialSlide}
        loop={loop}
        keyboard={keyboard}
        watchSlidesProgress={watchSlidesProgress}
        lazy={lazy}
        navigation={getNavigationConfig(navigation)}
        breakpoints={breakpoints}
        preventClicks={preventClicks}
        preventClicksPropagation={preventClicksPropagation}
        mousewheel={mousewheel}
        effect={effect}
        centeredSlides={centeredSlides}
        spaceBetween={spaceBetween}
        coverflowEffect={coverflowEffect}
        slideActiveClass={slideActiveClass}
        _containerclasses={containerClass}
        pagination={pagination ? { dynamicBullets: pagination } : pagination}
        onSlideChange={onSlideChange}
        speed={speed}
        onSwiper={onSwiper}
    >
        {
            children.map((item, index) => (
                <SwiperSlide key={item.key || index}>
                    {item}
                </SwiperSlide>))
        }
    </Swiper>));

Slider.propTypes = exact({
    autoplay: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object,
    ]),
    slidesPerView: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    children: PropTypes.node.isRequired,
    grabCursor: PropTypes.bool,
    initialSlide: PropTypes.number,
    clickable: PropTypes.bool,
    loop: PropTypes.bool,
    keyboard: PropTypes.bool,
    watchSlidesProgress: PropTypes.bool,
    lazy: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object,
    ]),
    navigation: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.object,
    ]),
    breakpoints: PropTypes.object,
    preventClicks: PropTypes.bool,
    preventClicksPropagation: PropTypes.bool,
    mousewheel: PropTypes.bool,
    effect: PropTypes.string,
    centeredSlides: PropTypes.bool,
    spaceBetween: PropTypes.number,
    coverflowEffect: PropTypes.object,
    slideActiveClass: PropTypes.string,
    containerClass: PropTypes.string,
    pagination: PropTypes.bool,
    onSlideChange: PropTypes.func,
    speed: PropTypes.number,
    onSwiper: PropTypes.func,
});

export default Slider;
